package com.solevo.dist.util

import com.solevo.dist.application.models.Order
import com.solevo.dist.application.models.QuoteStatus
import com.solevo.dist.application.models.InvoiceStatus
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.i18n.Localization
import com.solevo.dist.util.Res

/**
 * Centralized status mapping utilities for the application.
 * Provides consistent status label, color, and icon mapping across all ViewModels.
 */
data class StatusInfo(
    val label: String,
    val colorHex: String,
    val iconSrc: String? = null
)

object StatusMapper {

    private val strings get() = Localization.strings.value
    
    /**
     * Maps Order.Status to StatusInfo
     */
    fun mapOrderStatus(status: Order.Status): StatusInfo {
        return when (status) {
            is Order.Status.Confirmed -> StatusInfo(
                label = strings.orderStatusConfirmed,
                colorHex = "#01AF57"
            )
            is Order.Status.Closed -> StatusInfo(
                label = strings.orderStatusClosed,
                colorHex = "#0F67EA"
            )
            is Order.Status.Cancelled -> StatusInfo(
                label = strings.orderStatusCancelled,
                colorHex = "#FF0033"
            )
            is Order.Status.Expired -> StatusInfo(
                label = strings.orderStatusExpired,
                colorHex = "#FF0033"
            )
            is Order.Status.Created -> StatusInfo(
                label = strings.orderStatusCreated,
                colorHex = "#FFBE3D"
            )
            is Order.Status.Error -> StatusInfo(
                label = strings.orderStatusError,
                colorHex = "#FF0033"
            )
            is Order.Status.Initialized -> StatusInfo(
                label = strings.orderStatusInitialized,
                colorHex = "#FFBE3D"
            )
        }
    }
    
    /**
     * Maps QuoteStatus to StatusInfo
     */
    fun mapQuoteStatus(status: QuoteStatus): StatusInfo {
        return when (status) {
            QuoteStatus.Awaiting -> StatusInfo(
                label = strings.quotesStatusAwaiting,
                colorHex = "#FFBE3D"
            )
            QuoteStatus.Received -> StatusInfo(
                label = strings.quotesStatusReceived,
                colorHex = "#0F67EA"
            )
            QuoteStatus.Error -> StatusInfo(
                label = strings.quotesStatusError,
                colorHex = "#DD3636"
            )
            QuoteStatus.Other -> StatusInfo(
                label = "Other",
                colorHex = "#0F67EA"
            )
        }
    }
    
    /**
     * Maps InvoiceStatus to StatusInfo (with icons for invoice-specific statuses)
     */
    fun mapInvoiceStatus(status: InvoiceStatus): StatusInfo {
        return when (status) {
            is InvoiceStatus.Overdue -> StatusInfo(
                label = strings.invoiceStatusOverdue,
                colorHex = "#DD3636",
                iconSrc = Res.Icon.OrderIcons.overdue
            )
            is InvoiceStatus.DueToPay -> StatusInfo(
                label = strings.invoiceStatusDue,
                colorHex = "#B17700",
                iconSrc = Res.Icon.OrderIcons.due
            )
            is InvoiceStatus.Closed -> StatusInfo(
                label = strings.invoiceStatusClosed,
                colorHex = "#01A36C",
                iconSrc = Res.Icon.OrderIcons.closed
            )
        }
    }
    
    /**
     * Maps Transaction.Status to StatusInfo
     */
    fun mapTransactionStatus(status: Transaction.Status): StatusInfo {
        return when (status) {
            is Transaction.Status.Closed -> StatusInfo(
                label = strings.invoiceStatusClosed,
                colorHex = "#01A36C"
            )
            is Transaction.Status.Open -> StatusInfo(
                label = strings.statementTabOpen,
                colorHex = "#6366F1"
            )
        }
    }
    
    /**
     * Maps status text (string) to StatusInfo for invoice-related statuses.
     * Used when status comes as a string (e.g., from StatementPage).
     */
    fun mapStatusText(statusText: String): StatusInfo {
        val lower = statusText.lowercase()
        return when {
            lower.contains("overdue") || lower.contains("atraso") || lower.contains("retard") -> StatusInfo(
                label = strings.invoiceStatusOverdue,
                colorHex = "#DD3636",
                iconSrc = Res.Icon.OrderIcons.overdue
            )
            lower.contains("closed") || lower.contains("fechada") || lower.contains("payée") || lower.contains("paga") -> StatusInfo(
                label = strings.invoiceStatusClosed,
                colorHex = "#01A36C",
                iconSrc = Res.Icon.OrderIcons.closed
            )
            lower.contains("due") || lower.contains("pago") || lower.contains("payé") -> StatusInfo(
                label = strings.invoiceStatusDue,
                colorHex = "#B17700",
                iconSrc = Res.Icon.OrderIcons.due
            )
            else -> StatusInfo(
                label = statusText,
                colorHex = "#888888"
            )
        }
    }
}

